import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, MapPin, AlertCircle, CreditCard, Check, QrCode, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { services, specialists, branches, generateTimeSlots } from '../data/mockData';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, isSameDay, isToday, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';

export default function AgendaPage() {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedBranch, setSelectedBranch] = useState(branches[0]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '' });
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  const timeSlots = useMemo(() => {
    setIsLoading(true);
    const slots = generateTimeSlots(selectedBranch.id, selectedDate);
    setTimeout(() => setIsLoading(false), 300);
    return slots;
  }, [selectedBranch, selectedDate]);

  const goToPreviousWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const handleSlotSelect = (slot) => {
    if (slot.status === 'full') return;
    setSelectedSlot(slot);
  };

  const handleProceedToCheckout = () => {
    if (!selectedService || !selectedSlot) {
      toast.error('Por favor selecciona un servicio y horario');
      return;
    }
    setCheckoutStep(1);
  };

  const handleConfirmBooking = () => {
    if (!customerInfo.name || !customerInfo.phone) {
      toast.error('Por favor completa tus datos');
      return;
    }
    
    const code = `CONF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setConfirmationCode(code);
    setIsConfirmed(true);
    setCheckoutStep(3);
    toast.success('¡Cita confirmada! Te enviamos los detalles por WhatsApp');
  };

  const resetBooking = () => {
    setSelectedSlot(null);
    setSelectedService(null);
    setCheckoutStep(0);
    setIsConfirmed(false);
    setCustomerInfo({ name: '', phone: '', email: '' });
  };

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-12" data-testid="agenda-page">
      {/* Header */}
      <section className="bg-cream py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl text-graphite mb-4">
            Agenda tu cita
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Selecciona fecha, hora y servicio. Confirma con un depósito opcional para asegurar tu lugar.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Branch selector */}
            <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-2">
              {branches.map((branch) => (
                <button
                  key={branch.id}
                  onClick={() => {
                    setSelectedBranch(branch);
                    setSelectedSlot(null);
                  }}
                  data-testid={`branch-btn-${branch.id}`}
                  className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition-colors ${
                    selectedBranch.id === branch.id
                      ? 'bg-graphite text-cream'
                      : 'bg-soft-gray/50 hover:bg-soft-gray'
                  }`}
                >
                  {branch.name}
                </button>
              ))}
            </div>

            {/* Week navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={goToPreviousWeek}
                className="p-2 rounded-full hover:bg-soft-gray transition-colors"
                data-testid="prev-week"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h3 className="font-heading text-lg text-graphite">
                {format(currentWeekStart, 'MMMM yyyy', { locale: es })}
              </h3>
              <button
                onClick={goToNextWeek}
                className="p-2 rounded-full hover:bg-soft-gray transition-colors"
                data-testid="next-week"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Week days */}
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day) => {
                const isPast = isBefore(day, new Date()) && !isToday(day);
                const isSelected = isSameDay(day, selectedDate);
                
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => {
                      if (!isPast) {
                        setSelectedDate(day);
                        setSelectedSlot(null);
                      }
                    }}
                    disabled={isPast}
                    data-testid={`day-${format(day, 'yyyy-MM-dd')}`}
                    className={`calendar-day flex-col ${
                      isSelected ? 'selected' : ''
                    } ${isToday(day) ? 'today' : ''} ${isPast ? 'disabled' : ''}`}
                  >
                    <span className="text-xs text-muted-foreground uppercase">
                      {format(day, 'EEE', { locale: es })}
                    </span>
                    <span className="text-lg font-medium">{format(day, 'd')}</span>
                  </button>
                );
              })}
            </div>

            {/* Time slots */}
            <div className="p-6 bg-white rounded-2xl border border-border/50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-heading text-lg">
                  {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                </h4>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-soft-gray" /> Lleno
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-copper/20 border border-copper" /> Casi lleno
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded border border-border" /> Disponible
                  </span>
                </div>
              </div>

              {isLoading ? (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {Array(12).fill(0).map((_, i) => (
                    <div key={i} className="skeleton-loading h-12 rounded-lg" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {timeSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => handleSlotSelect(slot)}
                      disabled={slot.status === 'full'}
                      data-testid={`slot-${slot.time}`}
                      className={`time-slot px-3 py-3 rounded-lg border text-sm font-medium relative ${
                        selectedSlot?.time === slot.time
                          ? 'border-copper bg-copper text-cream'
                          : slot.status === 'full'
                          ? 'full'
                          : slot.status === 'limited'
                          ? 'limited'
                          : 'border-border hover:border-copper available'
                      }`}
                    >
                      {slot.time}
                      {slot.isPeakHour && slot.status !== 'full' && (
                        <Badge className="badge-peak absolute -top-2 -right-2 text-[10px] px-1">
                          Pico
                        </Badge>
                      )}
                      {slot.status === 'limited' && slot.remaining && (
                        <span className="block text-[10px] mt-0.5">
                          Quedan {slot.remaining}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Peak hours info */}
              <div className="mt-4 p-3 bg-muted-red/5 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-muted-red mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  <strong className="text-muted-red">Horas pico:</strong> 11:00-13:00 y 17:00-19:00. 
                  Te recomendamos reservar con anticipación o elegir horas valle para mejor disponibilidad.
                </p>
              </div>
            </div>

            {/* Service selector */}
            <div className="p-6 bg-white rounded-2xl border border-border/50">
              <h4 className="font-heading text-lg mb-4">Selecciona servicio</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {services.slice(0, 6).map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service)}
                    data-testid={`agenda-service-${service.id}`}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      selectedService?.id === service.id
                        ? 'border-copper bg-copper/5'
                        : 'border-border/50 hover:border-copper/50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" /> {service.duration} min
                        </p>
                      </div>
                      <span className="font-heading text-copper">${service.price}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Checkout Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 p-6 bg-white rounded-2xl border border-border/50 shadow-premium">
              <h3 className="font-heading text-xl mb-6">
                {isConfirmed ? 'Cita Confirmada' : 'Resumen'}
              </h3>

              {!isConfirmed && checkoutStep === 0 && (
                <>
                  {/* Summary */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3 p-3 bg-soft-gray/30 rounded-lg">
                      <MapPin className="w-5 h-5 text-copper" />
                      <div>
                        <p className="font-medium text-sm">{selectedBranch.name}</p>
                        <p className="text-xs text-muted-foreground">{selectedBranch.address}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-soft-gray/30 rounded-lg">
                      <CalendarIcon className="w-5 h-5 text-copper" />
                      <div>
                        <p className="font-medium text-sm">
                          {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedSlot ? selectedSlot.time : 'Selecciona un horario'}
                        </p>
                      </div>
                    </div>

                    {selectedService && (
                      <div className="p-3 bg-soft-gray/30 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{selectedService.name}</p>
                            <p className="text-xs text-muted-foreground">{selectedService.duration} min</p>
                          </div>
                          <span className="font-heading text-copper">${selectedService.price}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleProceedToCheckout}
                    disabled={!selectedSlot || !selectedService}
                    className="w-full bg-copper hover:bg-copper-dark text-cream py-3 rounded-full"
                    data-testid="proceed-checkout"
                  >
                    Continuar al checkout
                  </Button>
                </>
              )}

              {!isConfirmed && checkoutStep === 1 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="checkout-step">
                    <span className="checkout-step-number">1</span>
                    <h4 className="font-medium mb-3">Tus datos</h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Tu nombre *"
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border"
                        data-testid="agenda-input-name"
                      />
                      <input
                        type="tel"
                        placeholder="WhatsApp *"
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border"
                        data-testid="agenda-input-phone"
                      />
                      <input
                        type="email"
                        placeholder="Email (opcional)"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() => setCheckoutStep(2)}
                    disabled={!customerInfo.name || !customerInfo.phone}
                    className="w-full bg-graphite hover:bg-graphite/90 text-cream py-3 rounded-full"
                  >
                    Continuar
                  </Button>
                </div>
              )}

              {!isConfirmed && checkoutStep === 2 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="checkout-step">
                    <span className="checkout-step-number">2</span>
                    <h4 className="font-medium mb-3">Método de pago</h4>
                    <div className="space-y-3">
                      {[
                        { id: 'efectivo', label: 'Efectivo en sucursal' },
                        { id: 'tarjeta', label: 'Tarjeta en sucursal' }
                      ].map((method) => (
                        <button
                          key={method.id}
                          onClick={() => setPaymentMethod(method.id)}
                          className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 ${
                            paymentMethod === method.id
                              ? 'border-copper bg-copper/5'
                              : 'border-border'
                          }`}
                        >
                          <CreditCard className="w-5 h-5 text-muted-foreground" />
                          <span className="text-sm">{method.label}</span>
                          {paymentMethod === method.id && (
                            <Check className="w-4 h-4 text-copper ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-copper/5 rounded-xl border border-copper/20">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={depositEnabled}
                        onChange={(e) => setDepositEnabled(e.target.checked)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-sm">Pagar depósito (${selectedService ? Math.round(selectedService.price * 0.2) : 0})</p>
                        <p className="text-xs text-muted-foreground">
                          Asegura tu cita. El resto se paga en sucursal.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setCheckoutStep(1)}
                      className="flex-1 py-3 rounded-full"
                    >
                      Atrás
                    </Button>
                    <Button
                      onClick={handleConfirmBooking}
                      className="flex-1 bg-copper hover:bg-copper-dark text-cream py-3 rounded-full"
                      data-testid="confirm-agenda-btn"
                    >
                      Confirmar
                    </Button>
                  </div>
                </div>
              )}

              {isConfirmed && (
                <div className="text-center space-y-6 animate-fade-in">
                  <div className="w-16 h-16 mx-auto bg-muted-green/20 rounded-full flex items-center justify-center">
                    <Check className="w-8 h-8 text-muted-green" />
                  </div>

                  <div>
                    <p className="text-muted-foreground text-sm mb-2">Código de confirmación</p>
                    <p className="font-heading text-2xl tracking-wider">{confirmationCode}</p>
                  </div>

                  <div className="w-32 h-32 mx-auto bg-white border-2 border-border rounded-xl flex items-center justify-center">
                    <QrCode className="w-20 h-20 text-graphite" />
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={() => toast.success('Agregado al calendario')}
                      className="w-full bg-graphite hover:bg-graphite/90 text-cream py-3 rounded-full"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Agregar al calendario
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetBooking}
                      className="w-full py-3 rounded-full"
                    >
                      Nueva cita
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
